/**
 * SCOPEai Conversational Review.
 *
 * Shown once the user has drawn their rooms but BEFORE the purchase screen.
 * Calls POST /api/scope/drafts/:id/ai-review which returns:
 *   { summary, questions[], risks[], tips[] }
 *
 * User can answer the questions (multi-choice + free text), or skip review
 * entirely. Either way we persist the choice to the draft and progress to
 * the purchase phase.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, AlertTriangle, Lightbulb, ArrowRight, ArrowLeft, RefreshCw, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScopeAIReview({ draftId, onBack, onContinue }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [review, setReview] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const run = async (force = false) => {
    if (!draftId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/scope/drafts/${draftId}/ai-review${force ? '?force=true' : ''}`
      );
      setReview(res.data?.review || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not run SCOPEai. You can still continue to documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const saveAnswers = async (skipped) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/scope/drafts/${draftId}/ai-review-answers`, {
        answers: skipped ? {} : answers,
        skipped: !!skipped,
      });
      onContinue?.();
    } catch {
      // Non-blocking — still progress the flow.
      onContinue?.();
    } finally {
      setSubmitting(false);
    }
  };

  const answered = review?.questions?.length
    ? review.questions.every((q) => answers[q.id]?.option)
    : true;

  const questions = review?.questions || [];
  const risks = review?.risks || [];
  const tips = review?.tips || [];
  const isFallback = review?.source === 'fallback';

  return (
    <div data-testid="scopeai-review-screen">
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <button
          onClick={onBack}
          data-testid="ai-review-back"
          style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to drawing
        </button>

        <div style={{ background: 'linear-gradient(135deg, #0F2742 0%, #1E3A8A 100%)', color: '#fff', borderRadius: 16, padding: '26px 28px', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#BFDBFE' }}>
              Step 3 of 4 · SCOPEai review
            </div>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.015em' }}>
            SCOPEai has reviewed your scope
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: '#DBEAFE', maxWidth: 720 }}>
            A senior UK QS prompt reads your drawn rooms + finishes and flags anything that moves the budget before you buy your documents. Skip if you just want to proceed.
          </p>
          {review?.summary && !isFallback && (
            <div data-testid="ai-review-summary" style={{ marginTop: 18, padding: '12px 14px', background: 'rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 14, lineHeight: 1.55 }}>
              {review.summary}
            </div>
          )}
        </div>

        {loading && (
          <div data-testid="ai-review-loading" style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '40px 30px', textAlign: 'center', color: '#475569' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
              <RefreshCw className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} />
              SCOPEai is reading your rooms… this can take up to 30 seconds.
            </div>
          </div>
        )}

        {error && !loading && (
          <div data-testid="ai-review-error" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '14px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
            {error}
            <button
              onClick={() => run(true)}
              style={{ marginLeft: 12, background: 'none', border: 'none', color: '#991B1B', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
              data-testid="ai-review-retry"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && isFallback && (
          <div data-testid="ai-review-fallback" style={{ background: '#FEFCE8', border: '1px solid #FDE68A', color: '#854D0E', padding: '14px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
            SCOPEai review is not available right now. You can continue to documents — your Schedule of Works will still be produced from your drawn rooms.
          </div>
        )}

        {!loading && questions.length > 0 && (
          <div data-testid="ai-review-questions-block" style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '22px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#1D4ED8', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>
              Questions for you ({questions.length})
            </div>
            {questions.map((q, idx) => (
              <div key={q.id} style={{ paddingBottom: 18, marginBottom: 18, borderBottom: idx < questions.length - 1 ? '1px solid #F1F5F9' : 'none' }} data-testid={`ai-question-${idx}`}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0F2742', marginBottom: 4 }}>
                  {idx + 1}. {q.question}
                </div>
                {q.why && <div style={{ fontSize: 12, color: '#64748B', marginBottom: 10 }}>{q.why}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {(q.options || []).map((opt) => {
                    const selected = answers[q.id]?.option === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: { option: opt, note: prev[q.id]?.note || '' } }))}
                        data-testid={`ai-option-${idx}-${opt.replace(/\s+/g, '-').slice(0, 20)}`}
                        style={{
                          padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                          border: selected ? '1.5px solid #1D4ED8' : '1.5px solid #CBD5E1',
                          background: selected ? '#EFF6FF' : '#fff',
                          color: selected ? '#1D4ED8' : '#334155',
                          cursor: 'pointer',
                        }}
                      >
                        {selected && <Check className="w-3 h-3" style={{ display: 'inline', marginRight: 6 }} />}
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {answers[q.id]?.option?.startsWith('Other') && (
                  <input
                    type="text"
                    placeholder="Add detail (optional)"
                    value={answers[q.id]?.note || ''}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], note: e.target.value } }))}
                    data-testid={`ai-note-${idx}`}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #CBD5E1', fontSize: 13, marginTop: 6 }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && risks.length > 0 && (
          <div data-testid="ai-review-risks-block" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '18px 20px', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#854D0E' }}>
              <AlertTriangle className="w-4 h-4" />
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Risks a surveyor would flag</div>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {risks.map((r, idx) => (
                <li key={r.id || idx} style={{ fontSize: 13, color: '#713F12', lineHeight: 1.55 }} data-testid={`ai-risk-${idx}`}>
                  <span style={{ fontWeight: 700 }}>{r.title}.</span> {r.detail}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && tips.length > 0 && (
          <div data-testid="ai-review-tips-block" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '18px 20px', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#065F46' }}>
              <Lightbulb className="w-4 h-4" />
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Value-engineering ideas</div>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tips.map((t, idx) => (
                <li key={t.id || idx} style={{ fontSize: 13, color: '#064E3B', lineHeight: 1.55 }} data-testid={`ai-tip-${idx}`}>
                  <span style={{ fontWeight: 700 }}>{t.title}.</span> {t.detail}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
            <button
              onClick={() => saveAnswers(true)}
              disabled={submitting}
              data-testid="ai-review-skip"
              style={{
                background: 'none', border: 'none', color: '#64748B', fontSize: 13,
                cursor: submitting ? 'default' : 'pointer', fontWeight: 600, textDecoration: 'underline',
              }}
            >
              Skip review &amp; continue
            </button>
            <button
              onClick={() => saveAnswers(false)}
              disabled={submitting || (questions.length > 0 && !answered)}
              data-testid="ai-review-continue"
              style={{
                background: '#0F2742', color: '#fff', border: 'none',
                padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: (submitting || (questions.length > 0 && !answered)) ? 'default' : 'pointer',
                opacity: (submitting || (questions.length > 0 && !answered)) ? 0.55 : 1,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {questions.length > 0
                ? (answered ? 'Save answers & continue to documents' : 'Answer each question to continue')
                : 'Continue to documents'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
